import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Brain, X, Check, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';

interface MemorySuggestion {
  memory_type: 'fact' | 'preference' | 'event' | 'action';
  content: string;
  rationale: string;
}

interface MemorySuggestionPopupProps {
  suggestions: MemorySuggestion[];
  workspaceId: string;
  api: any;
  onDismiss: (index: number) => void;
  onDismissAll: () => void;
  onSave: (index: number) => void;
}

const MemorySuggestionPopup: React.FC<MemorySuggestionPopupProps> = ({
  suggestions,
  workspaceId,
  api,
  onDismiss,
  onDismissAll,
  onSave,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [handled, setHandled] = useState<Set<number>>(new Set());

  // Get indices of unhandled suggestions
  const unhandledIndices = useMemo(
    () => suggestions.map((_, idx) => idx).filter((idx) => !handled.has(idx)),
    [suggestions, handled]
  );

  // Find next unhandled index after current
  const findNextUnhandled = useCallback(
    (fromIndex: number): number | null => {
      for (let i = fromIndex + 1; i < suggestions.length; i++) {
        if (!handled.has(i)) return i;
      }
      return null;
    },
    [suggestions.length, handled]
  );

  // Find previous unhandled index before current
  const findPreviousUnhandled = useCallback(
    (fromIndex: number): number | null => {
      for (let i = fromIndex - 1; i >= 0; i--) {
        if (!handled.has(i)) return i;
      }
      return null;
    },
    [handled]
  );

  // Reset internal state when suggestions change
  useEffect(() => {
    console.log('[MemorySuggestionPopup] Suggestions changed, resetting state');
    setCurrentIndex(0);
    setHandled(new Set());
  }, [suggestions]);

  // Debug logging
  useEffect(() => {
    console.log('[MemorySuggestionPopup] Rendered with:', {
      suggestionsCount: suggestions.length,
      currentIndex,
      handled: Array.from(handled),
      unhandledCount: unhandledIndices.length,
      currentSuggestion: suggestions[currentIndex],
    });
  }, [suggestions, currentIndex, handled, unhandledIndices]);

  // Auto-dismiss after 30 seconds (just closes popup, no rejections recorded)
  useEffect(() => {
    if (suggestions.length === 0 || unhandledIndices.length === 0) return;

    const timer = setTimeout(() => {
      console.log('[MemorySuggestionPopup] Auto-dismissing after 30 seconds');
      onDismissAll();
    }, 30000);

    return () => clearTimeout(timer);
  }, [suggestions, unhandledIndices, onDismissAll]);

  // Handle Escape key to dismiss all
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismissAll();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismissAll]);

  // Auto-advance to next unhandled if current becomes handled
  useEffect(() => {
    if (handled.has(currentIndex) && unhandledIndices.length > 0) {
      const nextIdx = findNextUnhandled(currentIndex) ?? findPreviousUnhandled(currentIndex);
      if (nextIdx !== null) {
        setCurrentIndex(nextIdx);
      }
    }
  }, [currentIndex, handled, unhandledIndices, findNextUnhandled, findPreviousUnhandled]);

  // Don't render if no unhandled suggestions
  if (suggestions.length === 0 || unhandledIndices.length === 0) {
    console.log('[MemorySuggestionPopup] Not rendering - no unhandled suggestions');
    return null;
  }

  const currentSuggestion = suggestions[currentIndex];
  if (!currentSuggestion || handled.has(currentIndex)) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.post(`/workspaces/${workspaceId}/memories/`, {
        memory_type: currentSuggestion.memory_type,
        content: currentSuggestion.content,
      });

      console.log('[MemorySuggestionPopup] Memory save task triggered:', response.data?.task_id);

      // Mark as handled
      setHandled((prev) => new Set(prev).add(currentIndex));
      onSave(currentIndex);
    } catch (err) {
      console.error('[MemorySuggestionPopup] Error saving memory:', err);
      alert('Failed to save memory. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (rejecting) return;

    setRejecting(true);

    // Call rejection endpoint to record the rejection
    try {
      await api.post(`/workspaces/${workspaceId}/memories/rejections`, {
        memory_type: currentSuggestion.memory_type,
        content: currentSuggestion.content,
      });
      console.log('[MemorySuggestionPopup] Rejected memory recorded');
    } catch (err) {
      console.error('[MemorySuggestionPopup] Failed to record rejected memory:', err);
    } finally {
      setRejecting(false);
    }

    // Mark as handled
    setHandled((prev) => new Set(prev).add(currentIndex));
    onDismiss(currentIndex);
  };

  const handlePrevious = () => {
    const prevIdx = findPreviousUnhandled(currentIndex);
    if (prevIdx !== null) {
      setCurrentIndex(prevIdx);
    }
  };

  const handleNext = () => {
    const nextIdx = findNextUnhandled(currentIndex);
    if (nextIdx !== null) {
      setCurrentIndex(nextIdx);
    }
  };

  const getMemoryTypeColor = (type: string) => {
    switch (type) {
      case 'fact':
        return 'bg-blue-500 text-white';
      case 'preference':
        return 'bg-purple-500 text-white';
      case 'event':
        return 'bg-green-500 text-white';
      case 'action':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Calculate current position in unhandled list for display
  const currentPositionInUnhandled = unhandledIndices.indexOf(currentIndex) + 1;

  return (
    <div className="fixed bottom-24 right-6 z-40 animate-slide-in-bottom">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl border-2 border-brand-primary/20 dark:border-brand-primary/30 max-w-md w-96 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-accent p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              <span className="font-semibold">Memory Suggestions</span>
            </div>
            <button
              onClick={onDismissAll}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close memory suggestions"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {unhandledIndices.length > 1 && (
            <div className="mt-2 text-sm opacity-90">
              {currentPositionInUnhandled} of {unhandledIndices.length}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-xs font-medium rounded ${getMemoryTypeColor(
                currentSuggestion.memory_type
              )}`}
            >
              {currentSuggestion.memory_type}
            </span>
          </div>

          <div>
            <p className="text-neutral-900 dark:text-white font-medium">
              {currentSuggestion.content}
            </p>
          </div>

          <div>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              <span className="font-medium">Why: </span>
              {currentSuggestion.rationale}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between gap-3">
          {/* Navigation */}
          <div className="flex items-center gap-1">
            {unhandledIndices.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  disabled={findPreviousUnhandled(currentIndex) === null || saving || rejecting}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNext}
                  disabled={findNextUnhandled(currentIndex) === null || saving || rejecting}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleReject}
              disabled={saving || rejecting}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {rejecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Reject
                </>
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || rejecting}
              className="px-4 py-2 text-sm font-medium bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorySuggestionPopup;
